import React from 'react';
import { Loader2, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

/**
 * DataTable - A professional, Excel-like data grid component.
 * 
 * @param {Array} columns - [{ header: 'Name', accessor: 'name', render: (val, row) => ... }]
 * @param {Array} data - The data to display
 * @param {Boolean} loading - Loading state
 * @param {Object} pagination - { current, total, onPageChange }
 * @param {Function} onRowClick - (row) => ...
 * @param {String} emptyMessage - Message to show when data is empty
 */
const DataTable = ({ 
  columns, 
  data = [], 
  loading = false, 
  pagination, 
  onRowClick,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search records…",
  emptyMessage = "No records found in this sector."
}) => {
  return (
    <div className="w-full bg-white border border-[#D8DDE6] rounded-md overflow-hidden">
      {onSearchChange ? (
        <div className="px-4 py-3 bg-[#F4F6F9] border-b border-[#D8DDE6]">
          <input
            type="text"
            value={searchValue || ''}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full max-w-sm"
          />
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F4F6F9] border-b border-[#D8DDE6]">
              {columns.map((col, i) => (
                <th key={i} className={`p-3 text-[11px] font-bold text-[#706E6B] ${col.className || ''}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-24 text-center bg-white">
                  <div className="flex flex-col items-center gap-6">
                    <Loader2 size={32} className="animate-spin text-[#0176D3]" />
                    <span className="text-[12px] font-bold text-[#706E6B] animate-pulse">Loading…</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-24 text-center bg-white">
                  <div className="flex flex-col items-center gap-6">
                    <div className="w-16 h-16 rounded-lg bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#706E6B] shadow-sm">
                       <AlertCircle size={32} strokeWidth={1.5} />
                    </div>
                    <span className="text-[12px] font-bold text-[#706E6B]">{emptyMessage}</span>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                 <tr 
                  key={row._id || row.id || rowIndex} 
                  onClick={() => onRowClick && onRowClick(row)}
                  className={`group transition-all hover:bg-[#F4F6F9] border-b border-[#D8DDE6] last:border-none ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {columns.map((col, colIndex) => (
                    <td key={colIndex} className={`p-3 text-[13px] text-[#080707] ${col.cellClassName || ''}`}>
                      {col.render ? col.render(row[col.accessor], row) : row[col.accessor]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {pagination && (
        <div className="px-6 py-4 bg-white border-t border-[#D8DDE6] flex items-center justify-between">
          <div className="text-[11px] font-bold text-[#706E6B] leading-none">
            Showing <span className="text-[#080707]">{(pagination.current - 1) * 10 + 1}</span> to <span className="text-[#080707]">{Math.min(pagination.current * 10, pagination.total)}</span> of <span className="text-[#080707]">{pagination.total}</span> entries
          </div>
          <div className="flex items-center gap-3">
            {pagination.pages ? (
              <div className="text-[11px] font-bold text-[#706E6B]">
                Page <span className="text-[#080707]">{pagination.current}</span> of <span className="text-[#080707]">{pagination.pages}</span>
              </div>
            ) : null}
            <button 
              disabled={pagination.current === 1 || loading}
              onClick={() => pagination.onPageChange(pagination.current - 1)}
              className="w-10 h-10 flex items-center justify-center rounded-md bg-white border border-[#D8DDE6] text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F4F6F9] disabled:opacity-50 transition-all active:scale-95 shadow-sm"
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              disabled={pagination.current * 10 >= pagination.total || loading}
              onClick={() => pagination.onPageChange(pagination.current + 1)}
              className="w-10 h-10 flex items-center justify-center rounded-md bg-white border border-[#D8DDE6] text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F4F6F9] disabled:opacity-50 transition-all active:scale-95 shadow-sm"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
